import { useState, useMemo } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import React from "react";

// Register ChartJS components once
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

interface RevenueData {
  labels: string[];
  data: number[];
}

interface RevenueChartProps {
  revenueData: Record<string, RevenueData>;
  isDarkMode: boolean;
}

// Using React.memo with proper typing
export const RevenueChart = React.memo(function RevenueChart({ revenueData, isDarkMode }: RevenueChartProps) {
  const [timeFilter, setTimeFilter] = useState<string>("daily");
  
  const currentData = useMemo(
    () => revenueData[timeFilter] || { labels: [], data: [] },
    [revenueData, timeFilter]
  );

  const lineChartData = useMemo(() => ({
    labels: currentData.labels,
    datasets: [{
      label: `${timeFilter.charAt(0).toUpperCase() + timeFilter.slice(1)} Revenue`,
      data: currentData.data,
      borderColor: "#059669",
      backgroundColor: "rgba(5, 150, 105, 0.1)",
      tension: 0.4,
      fill: true,
    }],
  }), [currentData, timeFilter]);

  const options = useMemo(() => ({
    responsive: true,
    plugins: {
      legend: { display: false },
      title: { display: false },
      tooltip: {
        backgroundColor: isDarkMode ? "#1f2937" : "#ffffff",
        titleColor: isDarkMode ? "#e5e7eb" : "#111827",
        bodyColor: isDarkMode ? "#e5e7eb" : "#111827",
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: { color: isDarkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)" },
        ticks: {
          callback: (tickValue: string | number) => `$${Number(tickValue).toLocaleString()}`,
          color: isDarkMode ? "#d1d5db" : "#6b7280",
          font: { family: "'font-display', sans-serif" },
        },
      },
      x: {
        grid: { display: false },
        ticks: {
          color: isDarkMode ? "#d1d5db" : "#6b7280",
          font: { family: "'font-display', sans-serif" },
        },
      },
    },
  }), [isDarkMode]);

  const filters = useMemo(() => ["daily", "weekly", "monthly", "yearly", "alltime"], []);

  return (
    <div
      className={`max-w-7xl mx-auto rounded-lg shadow-lg p-4 sm:p-6 ${
        isDarkMode ? "bg-gray-900" : "bg-gray-50"
      }`}
    >
      <div className="flex flex-col sm:flex-row justify-between items-center mb-6">
        <h3
          className={`font-display text-xl sm:text-2xl font-bold ${
            isDarkMode ? "text-white" : "text-gray-900"
          } mb-2 sm:mb-0`}
        >
          Revenue Trend
        </h3>
        <div className="flex flex-wrap gap-2 justify-center">
          {filters.map((filter) => (
            <button
              key={filter}
              onClick={() => setTimeFilter(filter)}
              className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                timeFilter === filter
                  ? "bg-emerald-600 text-white shadow-md"
                  : `${
                      isDarkMode
                        ? "bg-gray-800 text-gray-400 hover:bg-gray-700"
                        : "bg-white text-gray-600 hover:bg-gray-100"
                    } border border-gray-300 dark:border-gray-700`
              }`}
            >
              {filter.charAt(0).toUpperCase() + filter.slice(1)}
            </button>
          ))}
        </div>
      </div>
      <div className="w-full h-[250px] sm:h-[300px]">
        <Line options={options} data={lineChartData} />
      </div>
    </div>
  );
});
